import { PropsWithChildren } from "react";
import type { LocationGenerics } from "@/router/locationGenerics";
import { useNavigateWithSlug } from "@/router";

import {
  ConnectionSource,
  IntegrationAbility,
  User,
  useConnectionWithCredentialsQuery,
} from "@/apollo/types";
import { PrimaryButton } from "@/components/elements/Button";
import DataBox, { DetailItem, Empty } from "@/components/elements/DataBox";
import IntegrationCellTitle from "@/components/elements/IntegrationCellTitle";
import SyncStatusLabel, { Mode } from "@/components/elements/SyncStatusLabel";
import { useDataSourceSlideOver } from "@/components/modules/new-data-source-slideover";
import { useViewDataSourceSlideOver } from "@/components/modules/view-data-source-slideover";
import { useMountEffect } from "@/hooks/useMountEffect";
import { IntegrationLogoBox, useIntegration } from "@/integrations";
import { useToast } from "@/providers/ToastProvider";
import dayjs from "@/utils/date";
import { useMatch, useSearch } from "@tanstack/react-location";

import { ConnectionAccessSection } from "./ConnectionAccessPanel";
import ConnectionNotifications from "./ConnectionNotifications";

export default function ViewConnectionPage() {
  const { params } = useMatch<LocationGenerics>();
  const { authFlowSuccess } = useSearch<{
    Search: {
      authFlowSuccess: boolean;
    };
  }>();
  const { data } = useConnectionWithCredentialsQuery({
    variables: {
      id: params.connectionId,
    },
  });
  const connection = data?.connection;
  const integration = useIntegration(connection?.integrationId);

  const navigate = useNavigateWithSlug();
  const toast = useToast();

  const dataSourceSlideOver = useDataSourceSlideOver();
  const { onOpen: viewSync } = useViewDataSourceSlideOver();

  useMountEffect(() => {
    if (authFlowSuccess) {
      toast(
        "Connector Authenticated",
        "You may now set up syncs using this connector",
        "success",
      );
      navigate({
        search: (search) => ({
          ...search,
          authFlowSuccess: undefined,
        }),
      });
    }
  });

  function stringifyUser(user?: Partial<User>) {
    if (!user) {
      return "Unknown";
    }

    if (!user.firstName) {
      return user.email;
    }

    return `${user.firstName} ${user.lastName} (${user.email})`;
  }
  function stringifyApiKey(name?: string) {
    if (!name) {
      return "Unknown (API Key)";
    }

    return `${name} (API Key)`;
  }

  if (!connection || !integration) {
    return null;
  }

  const hasAbility = (ability: IntegrationAbility) => {
    return integration && integration.abilities.includes(ability);
  };
  const hasSync =
    connection.eltSyncs.length > 0 || connection.reverseEltSyncs.length > 0;

  // Null if the connection was created directly inside Weld to not show the "Connection source" field
  const connectionSource =
    connection.source === ConnectionSource.Api
      ? "Weld Connect"
      : connection.connectedExternally
        ? "Connected using Authorization Link"
        : null;

  return (
    <div>
      <div className="flex flex-col space-y-8">
        <ConnectionNotifications connection={connection} />
        <DataBox header="Details">
          <div className="">
            <div className="flex flex-col space-y-4">
              <DetailItem title="Connector">
                <IntegrationCellTitle
                  integrationId={connection.integrationId}
                  connectionLabel={integration?.name}
                />
              </DetailItem>
              <DetailItem title="Name">{connection.label}</DetailItem>
              <DetailItem title="Last connected by">
                {connection.source === ConnectionSource.Api
                  ? stringifyApiKey(connection.apiKey?.name)
                  : stringifyUser(connection?.user ?? undefined)}
              </DetailItem>
              <DetailItem title="Last connected at">
                {dayjs(connection.connectedAt).format("MMMM D, YYYY")}
              </DetailItem>
              {connectionSource && (
                <DetailItem title="Connection source">
                  {connectionSource}
                </DetailItem>
              )}
              {connection.credentials?.client_email ? (
                <DetailItem title="E-mail">
                  {connection.credentials.client_email}
                </DetailItem>
              ) : null}
            </div>
          </div>
        </DataBox>
        <ConnectionAccessSection connectionId={connection.id} />
        {!integration.isUtilityConnector && (
          <DataBox header="Syncs">
            <>
              {hasSync && (
                <SyncTable>
                  {connection.eltSyncs.map((sync) => (
                    <SyncItem
                      integrationId={
                        sync?.destinationConnection?.integrationId || ""
                      }
                      key={sync.id}
                      label={sync?.destinationSchemaName || ""}
                      {...sync}
                      onClick={() => viewSync({ syncId: sync.id })}
                      type="Data Source"
                    />
                  ))}
                  {connection.reverseEltSyncs.map((sync) => (
                    <SyncItem
                      label={sync?.primaryObject || ""}
                      integrationId={
                        sync?.destinationConnection?.integrationId || ""
                      }
                      key={sync.id}
                      {...sync}
                      onClick={() =>
                        navigate({ to: `/reverse-etl/${sync.id}` })
                      }
                      type="Reverse ETL"
                    />
                  ))}
                </SyncTable>
              )}

              {!hasSync && (
                <Empty>
                  <p>There are currently no syncs using this connector.</p>
                  <div className="my-4 flex flex-row gap-4">
                    {hasAbility(IntegrationAbility.EltSource) && (
                      <PrimaryButton
                        onClick={() => {
                          dataSourceSlideOver.onOpen({
                            connectionId: connection.id,
                          });
                        }}
                      >
                        + New Data Source Sync
                      </PrimaryButton>
                    )}
                    {hasAbility(IntegrationAbility.ActivateDestination) && (
                      <PrimaryButton
                        onClick={() =>
                          navigate({
                            to: "/reverse-etl/new",
                            search: {
                              connection_id: connection.id,
                            },
                          })
                        }
                      >
                        + New Reverse ETL Sync
                      </PrimaryButton>
                    )}
                  </div>
                </Empty>
              )}
            </>
          </DataBox>
        )}
      </div>
    </div>
  );
}

const SyncItem = (props: {
  status: string;
  integrationId: string;
  createdAt: string;
  label: string;
  type: string;
  onClick: () => void;
}) => (
  <tr
    className="h-16 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
    onClick={props.onClick}
  >
    <td className="flex h-16 items-center justify-start text-sm dark:text-white">
      <IntegrationLogoBox id={props.integrationId} size="sm" />
      <span className="ml-3">{props.label}</span>
    </td>
    <td className="text-sm dark:text-white">
      <span>{props.type}</span>
    </td>
    <td className="text-sm dark:text-white">
      <SyncStatusLabel mode={props.status as Mode} />
    </td>
    <td className="text-sm">
      <div className="flex dark:text-white">
        {dayjs(props.createdAt).fromNow()}
      </div>
    </td>
  </tr>
);

const SyncTable = (props: PropsWithChildren) => (
  <table className="mt-0 w-full table-auto">
    <thead>
      <tr className="text-left text-xs font-light text-gray-600 dark:text-gray-400">
        <th className="font-light">Connector</th>
        <th className="font-light">Type</th>
        <th className="font-light">Status</th>
        <th className="font-light">Created</th>
      </tr>
    </thead>
    <tbody>{props.children}</tbody>
  </table>
);
